import React from 'react';
import { useFormikContext } from 'formik';
import { Box, Card, Flex, Radio, Text } from 'pounceV2';
import { OutputLength } from 'Generated/schema';
import { useTheme } from '@mui/material';
import { persistAiOutputLengthSetting, RerunFormValues } from './GenericAISummaryContextV3';

export function mapOutputLengthToInitial(length: OutputLength): string {
  switch (length) {
    case OutputLength.Small:
      return 'B';
    case OutputLength.Medium:
      return 'S';
    case OutputLength.Large:
    case OutputLength.Largest:
    case OutputLength.Xlarge:
      return 'A';
    default:
      return '?';
  }
}

export function mapOutputLengthToLabel(length: OutputLength): string {
  switch (length) {
    case OutputLength.Small:
      return 'Basic';
    case OutputLength.Medium:
      return 'Standard';
    case OutputLength.Large:
    case OutputLength.Largest:
    case OutputLength.Xlarge:
      return 'Advanced';
    default:
      return length.toString();
  }
}

export const REASONING_LEVEL_OPTIONS = [
  { value: OutputLength.Small, label: 'Basic', description: 'Simpler, faster reasoning' },
  {
    value: OutputLength.Medium,
    label: 'Standard',
    description: 'Balanced reasoning (recommended)',
  },
  {
    value: OutputLength.Largest,
    label: 'Advanced',
    description: 'Deep, comprehensive reasoning',
  },
];

export interface ReasoningLevelCardProps {
  outputLength: OutputLength;
  onOutputLengthChange: (newLength: OutputLength) => void;
}

export const ReasoningLevelCard: React.FC<ReasoningLevelCardProps> = ({
  outputLength,
  onOutputLengthChange,
}) => {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();

  return (
    <Card
      p={6}
      border={`1px solid ${tokens.stroke.neutral[5]}`}
      style={{
        width: '297px',
        backgroundColor: componentTokens.selectMenuItems.color.item.background.containerBase,
      }}
    >
      Edit prompt settings
      <Text
        mt={4}
        mb={3}
        color={tokens.content.text.accent[14][2]}
        variant="body/sm"
        fontWeight="medium"
      >
        REASONING LEVEL
      </Text>
      <Flex direction="column" gap={3}>
        {REASONING_LEVEL_OPTIONS.map(option => (
          <Box key={option.value}>
            <Radio
              checked={outputLength === option.value}
              onChange={() => onOutputLengthChange(option.value)}
              value={option.value}
              aria-label={option.label}
              label={option.label}
            />
            <Text ml="39px" mt={0.5} variant="label/sm" color={tokens.content.text.neutral[3]}>
              {option.description}
            </Text>
          </Box>
        ))}
      </Flex>
    </Card>
  );
};

const AIPromptSettingsV3 = ({ outputLength }: { outputLength: OutputLength }) => {
  const { setFieldValue, values } = useFormikContext<RerunFormValues>();

  const handleChange = React.useCallback(
    (newLength: OutputLength) => {
      persistAiOutputLengthSetting(newLength, values.kind);
      setFieldValue('outputLength', newLength);
    },
    [setFieldValue, values.kind],
  );

  return <ReasoningLevelCard outputLength={outputLength} onOutputLengthChange={handleChange} />;
};

export default AIPromptSettingsV3;
